import type { TaskContext } from "@pdfking/taskmaker"
import type { Bytes } from "@pdfking/tfunc"
import type { PdfEngineOptions } from "../types/engine/PdfEngineOptions"
import type { RemoteFile } from "../types/files/cached/RemoteFile"
import type { RemoteFileChunk } from "../types/files/cached/RemoteFileChunk"
import type { EngineTasks } from "../types/tasks/EngineTasks"
import { BlobCreate } from "@pdfking/compatibility"
import {
  ResizeUInt8,
  ResponseContentLength,
  SafePromise,
  StreamToIterable,
} from "@pdfking/tfunc"
import { EngineError } from "../errors/EngineError"
import { GenerateFile } from "../files/GenerateFile"
import { ProgressFetch } from "../network/ProgressFetch"
import { ErrorName } from "../types/errors/ErrorName"
import { ContentType } from "../types/files/ContentType"
import { VirtualFileType } from "../types/files/VirtualFileType"
import { TrackedTaskName } from "../types/tasks/TrackedTaskName"

export const TaskDownload = async function* ({
  emit,
  engine,
  options,
  source: remote,
}: TaskContext<
  EngineTasks,
  {
    readonly options: PdfEngineOptions
    readonly source: RemoteFile
  }
>): AsyncGenerator<RemoteFileChunk> {
  const { chunk_size } = options

  for (const url of remote.mirrors) {
    const { value: response } = await SafePromise(
      ProgressFetch(url, {
        download: e =>
          emit({
            name: TrackedTaskName.DOWNLOAD,
            ...e,
          }),
      })
    )

    if (response?.ok) {
      const stream = ResizeUInt8(StreamToIterable(response.body), chunk_size)
      const bytes_total = ResponseContentLength(response)

      let bytes_start = 0 as Bytes

      for await (const chunk of stream) {
        const bytes_end = (bytes_start + chunk.length) as Bytes

        yield {
          enum: VirtualFileType.REMOTE_FILE_CHUNK,
          bytes_end,
          bytes_start,
          bytes_total,
          content: await GenerateFile(
            engine,
            options,
            [BlobCreate(chunk)],
            ContentType.BYTES,
            emit
          ),
          file_meta: remote.file_meta,
          remote,
        } as RemoteFileChunk

        bytes_start = bytes_end
      }

      return
    }
  }

  throw EngineError(ErrorName.FILE_NOT_FOUND)
}
